import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Shield, User, Mail, Lock, Save, Settings, Image, Upload, X } from 'lucide-react';

interface UpdateProfileForm {
  name: string;
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const AdminSettings: React.FC = () => {
  const { agent } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [settingsSuccessMessage, setSettingsSuccessMessage] = useState('');
  const [settingsErrorMessage, setSettingsErrorMessage] = useState('');
  const [logoSuccessMessage, setLogoSuccessMessage] = useState('');
  const [logoErrorMessage, setLogoErrorMessage] = useState('');
  const [systemName, setSystemName] = useState('Itororo SAC');
  const [systemLogo, setSystemLogo] = useState<string | undefined>();
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [formData, setFormData] = useState<UpdateProfileForm>({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Password validation helpers
  const isChangingPassword = formData.newPassword || formData.confirmPassword || formData.currentPassword;
  const passwordsMatch = formData.newPassword === formData.confirmPassword;
  const passwordLengthValid = formData.newPassword.length >= 6 || formData.newPassword.length === 0;
  const hasCurrentPassword = formData.currentPassword.length > 0;

  const getPasswordValidationMessage = () => {
    if (!isChangingPassword) return null;

    if (formData.newPassword && !passwordLengthValid) {
      return { type: 'error', message: 'A nova senha deve ter pelo menos 6 caracteres' };
    }

    if (formData.confirmPassword && !passwordsMatch) {
      return { type: 'error', message: 'As senhas não coincidem' };
    }

    if ((formData.newPassword || formData.confirmPassword) && !hasCurrentPassword) {
      return { type: 'error', message: 'Senha atual é obrigatória para alterar a senha' };
    }

    if (formData.newPassword && formData.confirmPassword && passwordsMatch && passwordLengthValid && hasCurrentPassword) {
      return { type: 'success', message: 'Senhas coincidem ✓' };
    }

    return null;
  };

  const isFormValid = () => {
    if (!formData.name || !formData.email) return false;

    if (isChangingPassword) {
      return passwordsMatch && passwordLengthValid && hasCurrentPassword && formData.newPassword.length > 0;
    }

    return true;
  };

  useEffect(() => {
    if (agent) {
      setFormData(prev => ({
        ...prev,
        name: agent.name,
        email: agent.email,
      }));
    }
    loadSettings();
  }, [agent]);

  useEffect(() => {
    // Update favicon when logo is loaded
    if (systemLogo) {
      updateFavicon(systemLogo);
    }
  }, [systemLogo]);

  const loadSettings = async () => {
    try {
      const settings = await api.getSettings();
      setSystemName(settings.systemName || 'Itororo SAC');
      setSystemLogo(settings.systemLogo);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setLogoErrorMessage('Tipo de arquivo inválido. Use apenas imagens (jpg, png, gif, webp, svg)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setLogoErrorMessage('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    setSelectedLogoFile(file);
    setLogoErrorMessage('');

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!selectedLogoFile) return;

    setIsUploadingLogo(true);
    setLogoSuccessMessage('');
    setLogoErrorMessage('');

    try {
      const result = await api.uploadSystemLogo(selectedLogoFile);
      setSystemLogo(result.logoUrl);
      setLogoSuccessMessage('Logo enviado com sucesso!');
      setSelectedLogoFile(null);
      setLogoPreview(null);

      // Update favicon
      updateFavicon(result.logoUrl);
    } catch (error: any) {
      console.error('Failed to upload logo:', error);
      setLogoErrorMessage(error.response?.data?.error || 'Falha ao enviar logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Tem certeza que deseja remover o logo do sistema?')) return;

    setIsUploadingLogo(true);
    setLogoSuccessMessage('');
    setLogoErrorMessage('');

    try {
      await api.deleteSystemLogo();
      setSystemLogo(undefined);
      setLogoSuccessMessage('Logo removido com sucesso!');

      // Reset favicon to default
      updateFavicon(null);
    } catch (error: any) {
      console.error('Failed to delete logo:', error);
      setLogoErrorMessage(error.response?.data?.error || 'Falha ao remover logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const updateFavicon = (logoUrl: string | null) => {
    const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = logoUrl ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}${logoUrl}` : '/vite.svg';
    document.getElementsByTagName('head')[0].appendChild(link);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    // Validate passwords match if changing password
    if (formData.newPassword || formData.confirmPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        setErrorMessage('As senhas não coincidem');
        return;
      }
      if (formData.newPassword.length < 6) {
        setErrorMessage('A nova senha deve ter pelo menos 6 caracteres');
        return;
      }
      if (!formData.currentPassword) {
        setErrorMessage('Senha atual é obrigatória para alterar a senha');
        return;
      }
    }

    setIsLoading(true);

    try {
      const updateData: any = {
        name: formData.name,
        email: formData.email,
      };

      if (formData.currentPassword && formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      await api.updateAdminProfile(updateData);

      setSuccessMessage('Perfil atualizado com sucesso!');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));

      // Reload page after 1.5 seconds to refresh the auth context
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      setErrorMessage(error.response?.data?.error || 'Falha ao atualizar perfil');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsSuccessMessage('');
    setSettingsErrorMessage('');

    if (!systemName.trim()) {
      setSettingsErrorMessage('O nome do sistema não pode estar vazio');
      return;
    }

    setIsLoadingSettings(true);

    try {
      await api.updateSettings({ systemName: systemName.trim() });
      setSettingsSuccessMessage('Configurações atualizadas com sucesso! Atualize a página para ver as mudanças.');
    } catch (error: any) {
      console.error('Failed to update settings:', error);
      setSettingsErrorMessage(error.response?.data?.error || 'Falha ao atualizar configurações');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg">
          <Shield className="w-8 h-8 text-purple-600 dark:text-purple-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Administração do Sistema</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Gerenciar configurações do sistema e perfil de administrador</p>
        </div>
      </div>

      {/* System Settings Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 max-w-2xl">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center space-x-2">
          <Settings className="w-6 h-6" />
          <span>Configurações do Sistema</span>
        </h2>

        {settingsSuccessMessage && (
          <div className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
            {settingsSuccessMessage}
          </div>
        )}

        {settingsErrorMessage && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
            {settingsErrorMessage}
          </div>
        )}

        <form onSubmit={handleSettingsSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nome do Sistema/Aplicação *
            </label>
            <input
              type="text"
              value={systemName}
              onChange={(e) => setSystemName(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              placeholder="Ex: Itororo SAC"
            />
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Este nome aparecerá na tela de login e no cabeçalho do sistema
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <button
              type="submit"
              disabled={isLoadingSettings}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded-lg transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingSettings ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Salvar Configurações</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* System Logo Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 max-w-2xl">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center space-x-2">
          <Image className="w-6 h-6" />
          <span>Logo do Sistema</span>
        </h2>

        {logoSuccessMessage && (
          <div className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
            {logoSuccessMessage}
          </div>
        )}

        {logoErrorMessage && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
            {logoErrorMessage}
          </div>
        )}

        <div className="space-y-6">
          {/* Current Logo Display */}
          {systemLogo && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Logo Atual
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 w-32 h-32 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden border-2 border-gray-300 dark:border-gray-600">
                  <img
                    src={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}${systemLogo}`}
                    alt="System Logo"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Este logo é usado como favicon (ícone da aba do navegador) e pode ser usado em outros lugares do sistema
                  </p>
                  <button
                    onClick={handleLogoDelete}
                    disabled={isUploadingLogo}
                    className="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <X className="w-4 h-4" />
                    <span>Remover Logo</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Upload New Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {systemLogo ? 'Alterar Logo' : 'Adicionar Logo'}
            </label>

            <div className="space-y-4">
              {/* File Input */}
              <div>
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/*"
                  onChange={handleLogoFileChange}
                  disabled={isUploadingLogo}
                  className="hidden"
                />
                <label
                  htmlFor="logo-upload"
                  className={`flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition ${
                    isUploadingLogo
                      ? 'opacity-50 cursor-not-allowed'
                      : 'border-gray-300 dark:border-gray-600 hover:border-purple-500 dark:hover:border-purple-400'
                  }`}
                >
                  <Upload className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Clique para selecionar uma imagem
                  </span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Formatos aceitos: JPG, PNG, GIF, WEBP, SVG (max 5MB)
                </p>
              </div>

              {/* Preview */}
              {logoPreview && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pré-visualização
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0 w-32 h-32 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden border-2 border-gray-300 dark:border-gray-600">
                      <img
                        src={logoPreview}
                        alt="Preview"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {selectedLogoFile?.name}
                      </p>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleLogoUpload}
                          disabled={isUploadingLogo}
                          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          {isUploadingLogo && (
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          )}
                          <Upload className="w-4 h-4" />
                          <span>{isUploadingLogo ? 'Enviando...' : 'Enviar Logo'}</span>
                        </button>
                        <button
                          onClick={() => {
                            setSelectedLogoFile(null);
                            setLogoPreview(null);
                          }}
                          disabled={isUploadingLogo}
                          className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Settings Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 max-w-2xl">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Informações do Perfil</h2>

        {successMessage && (
          <div className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Role Display */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-medium text-purple-900 dark:text-purple-300">
                Função: Administrador do Sistema
              </span>
            </div>
          </div>

          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>Nome *</span>
              </div>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              placeholder="Seu nome completo"
            />
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>Email *</span>
              </div>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              placeholder="seu@email.com"
            />
          </div>

          {/* Password Change Section */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <Lock className="w-5 h-5" />
              <span>Alterar Senha</span>
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Deixe os campos de senha em branco se não deseja alterá-la
            </p>

            <div className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Senha Atual {isChangingPassword && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="password"
                  value={formData.currentPassword}
                  onChange={(e) => {
                    setFormData({ ...formData, currentPassword: e.target.value });
                    setErrorMessage('');
                  }}
                  className={`w-full px-4 py-2 border ${
                    isChangingPassword && !hasCurrentPassword
                      ? 'border-red-300 dark:border-red-600'
                      : 'border-gray-300 dark:border-gray-600'
                  } dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                  placeholder="Sua senha atual"
                />
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nova Senha
                </label>
                <input
                  type="password"
                  value={formData.newPassword}
                  onChange={(e) => {
                    setFormData({ ...formData, newPassword: e.target.value });
                    setErrorMessage('');
                  }}
                  className={`w-full px-4 py-2 border ${
                    formData.newPassword && !passwordLengthValid
                      ? 'border-red-300 dark:border-red-600'
                      : 'border-gray-300 dark:border-gray-600'
                  } dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                  placeholder="Mínimo 6 caracteres"
                  minLength={6}
                />
                {formData.newPassword && !passwordLengthValid && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    ⚠️ Mínimo 6 caracteres
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirmar Nova Senha
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    setFormData({ ...formData, confirmPassword: e.target.value });
                    setErrorMessage('');
                  }}
                  className={`w-full px-4 py-2 border ${
                    formData.confirmPassword && !passwordsMatch
                      ? 'border-red-300 dark:border-red-600'
                      : formData.confirmPassword && passwordsMatch
                      ? 'border-green-300 dark:border-green-600'
                      : 'border-gray-300 dark:border-gray-600'
                  } dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                  placeholder="Digite a nova senha novamente"
                  minLength={6}
                />
                {formData.confirmPassword && !passwordsMatch && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    ⚠️ As senhas não coincidem
                  </p>
                )}
                {formData.confirmPassword && passwordsMatch && formData.newPassword && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    ✓ As senhas coincidem
                  </p>
                )}
              </div>

              {/* Overall validation message */}
              {(() => {
                const validationMsg = getPasswordValidationMessage();
                if (!validationMsg) return null;

                return (
                  <div className={`p-3 rounded-lg ${
                    validationMsg.type === 'error'
                      ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
                      : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                  }`}>
                    <p className="text-sm">{validationMsg.message}</p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center space-x-3 pt-4">
            <button
              type="submit"
              disabled={isLoading || !isFormValid()}
              className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded-lg transition flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Salvar Alterações</span>
                </>
              )}
            </button>
          </div>
          {!isFormValid() && isChangingPassword && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center">
              Complete todos os campos de senha corretamente para salvar as alterações
            </p>
          )}
        </form>
      </div>
    </div>
  );
};
